import { useEffect, useState } from 'react'
import { api, ApiError, finishSignIn, signIn, signOut } from './api'
import { Home } from './Home'
import { ProjectView } from './project/ProjectView'
import { ShareGate } from './ShareGate'
import { ProjectGate } from './ProjectGate'
import { Logo, Spinner } from './ui'

type Me = { team: { email: string; name: string } | null; anthropic: boolean }

export function useRoute() {
  const [path, setPath] = useState(location.pathname)
  useEffect(() => {
    const on = () => setPath(location.pathname)
    window.addEventListener('popstate', on)
    return () => window.removeEventListener('popstate', on)
  }, [])
  return path
}

export function navigate(to: string) {
  if (to === location.pathname) return
  history.pushState(null, '', to)
  window.dispatchEvent(new PopStateEvent('popstate'))
}

export function App() {
  const path = useRoute()
  const [me, setMe] = useState<Me | null>(null)
  const [authError, setAuthError] = useState('')

  useEffect(() => {
    ;(async () => {
      try {
        const err = await finishSignIn()
        if (err) setAuthError(err)
      } catch (e) {
        setAuthError(e instanceof ApiError ? e.message : 'Sign-in failed.')
      }
      try {
        setMe(await api.get<Me>('me'))
      } catch {
        setMe({ team: null, anthropic: false })
      }
    })()
  }, [])

  const share = path.match(/^\/s\/([a-z0-9]+)\/?$/)
  if (share) return <ShareGate token={share[1]} />

  if (!me) return <FullPage><Spinner /></FullPage>

  const proj = path.match(/^\/(?:p\/|projects\/(?=[a-z0-9]+\/transcripts))([a-z0-9]+)/)
  if (!me.team && proj && !authError) return <ProjectGate projectId={proj[1]} />

  if (!me.team) {
    return (
      <FullPage>
        <div className="signin">
          <Logo size={44} />
          <h1>Osmo Selects</h1>
          <p>Interview transcripts next to the footage, for picking the story.</p>
          <button className="btn btn-primary btn-lg" onClick={signIn}>Continue with Google</button>
          <p className="muted small">For the Osmo team (osmo.inc accounts). Clients: open the link in the email Osmo sent you.</p>
          {authError && <p className="error">{authError}</p>}
        </div>
      </FullPage>
    )
  }

  // The team works on post.osmo.inc, where a project's transcripts sit inside the project.
  if (proj && location.hostname === 'transcripts.osmo.inc') {
    location.replace(`https://post.osmo.inc/projects/${proj[1]}/transcripts${location.search}`)
    return <FullPage><Spinner /></FullPage>
  }
  if (proj) return <ProjectView projectId={proj[1]} anthropic={me.anthropic} />
  // The team's list of projects is on post.osmo.inc; this site's home is only for clients' links.
  if (location.hostname === 'transcripts.osmo.inc') {
    location.replace('https://post.osmo.inc/')
    return <FullPage><Spinner /></FullPage>
  }
  return <Home me={me.team} onSignOut={async () => { await signOut(); location.href = '/' }} />
}

function FullPage({ children }: { children: React.ReactNode }) {
  return <div className="fullpage">{children}</div>
}
